<template>
    <app-layout>
        <base-header :shadow="false" border border-bg-from="from-orange-500" border-bg-to="to-red-500"
                     :back-url="route('stores.profit-and-loss.select')"
        >
            <div class="flex items-center justify-between w-full">
                <div>
                    <h4 class="text-xl font-medium">
                        {{ $t('general.profit_and_loss') }}
                    </h4>

                    <span v-if="store">
                        {{ store.name }}
                    </span>
                </div>

                <div class="flex items-center">
                    <!-- TODO: Actions if necessary -->
                </div>
            </div>
        </base-header>

        <table-base class="text-sm" :busy="isLoading">
            <template #head>
                <table-head>{{ $t('date.date') }}</table-head>
                <table-head>{{ $t('general.budget') }}</table-head>
                <table-head>{{ $t('general.projection') }}</table-head>
                <table-head>{{ $t('general.extrapolations') }}</table-head>
                <table-head>{{ $t('general.manual_profit_and_loss') }}</table-head>
                <table-head>{{ $t('general.actual') }}</table-head>
                <table-head></table-head>
            </template>
            <template #body v-if="!isLoading">
                <table-row v-for="month in monthRange" :key="month">
                    <table-cell>
                        {{ $moment(month + '-' + currentYear, 'M-Y').format('MMMM YYYY') }}
                    </table-cell>
                    <table-cell>
                        <icon v-if="hasBudget(currentYear, month)" :size="6" name="check-circle"
                              class-icon="text-green-600"
                        ></icon>
                        <icon v-else :size="6" name="x-circle" class-icon="text-red-600"></icon>
                    </table-cell>
                    <table-cell>
                        <icon v-if="hasProjection(currentYear, month)" :size="6" name="check-circle"
                              class-icon="text-green-600"
                        ></icon>
                        <icon v-else :size="6" name="x-circle" class-icon="text-red-600"></icon>
                    </table-cell>
                    <table-cell>
                        {{ getExtrapolationCount(currentYear, month) }} / {{ $helper.getExtrapolationCountForMonth(
                        currentYear, month) }}
                    </table-cell>
                    <table-cell>
                        <icon v-if="hasProfitAndLoss(currentYear, month)" :size="6" name="check-circle"
                              class-icon="text-green-600"
                        ></icon>
                        <icon v-else :size="6" name="x-circle" class-icon="text-red-600"></icon>
                    </table-cell>
                    <table-cell>
                        <icon v-if="hasActual(currentYear, month)" :size="6" name="check-circle"
                              class-icon="text-green-600"
                        ></icon>
                        <icon v-else :size="6" name="x-circle" class-icon="text-red-600"></icon>
                    </table-cell>
                    <table-cell class="flex justify-end space-x-1">
                        <default-button icon="pencil" size="xs"
                                        :href="route('stores.profit-and-loss.edit', [store.id, currentYear, month])"
                        />
                    </table-cell>
                </table-row>
            </template>
        </table-base>

        <!-- Custom pagination with years -->
        <div v-if="!isLoading"
             class="sticky bottom-0 border-t border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-600 px-4 flex items-center justify-between sm:px-0"
        >
            <div class="w-0 flex-1 flex">
                <button type="button" v-if="currentYear > yearRange[0]" aria-label="Previous" @click="--currentYear"
                        class="-mt-px border-t-2 pl-6 border-transparent pt-6 pb-6 pr-1 inline-flex items-center text-sm leading-5 font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-400 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-300 transition"
                >
                    <icon name="arrow-left" class-icon="mr-3 h-5 w-5" fill="currentColor"/>
                    {{ $t('pagination.previous') }}
                </button>
            </div>
            <div class="hidden md:flex">
                <span
                    class="border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-500 focus:text-gray-700 focus:border-gray-500 hover:bg-gray-200 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-300 dark:hover:bg-gray-900 -mt-px border-t-2 pt-6 pb-6 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition"
                >
                    ...
                </span>
                <template v-for="(year, key) in yearRange">
                    <button type="button" @click="currentYear = year" :key="key"
                            v-if="year === yearRange[0] || year === yearRange[yearRange.length - 1] || (currentYear - year <= 3 && currentYear - year >= -3)"
                            :class="year === currentYear ? 'border-indigo-500 bg-indigo-100 text-indigo-600 focus:text-indigo-800 focus:border-indigo-700 hover:text-indigo-700 hover:border-indigo-700 hover:bg-indigo-200 dark:border-indigo-300 dark:bg-indigo-600 dark:text-indigo-300 dark:focus:text-indigo-200 dark:focus:border-indigo-200 dark:hover:text-indigo-200 dark:hover:border-indigo-200 dark:hover:bg-indigo-700' : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-500 focus:text-gray-700 focus:border-gray-500 hover:bg-gray-200 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-300 dark:hover:bg-gray-900'"
                            class="-mt-px border-t-2 pt-6 pb-6 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition"
                    >
                        {{ year }}
                    </button>
                </template>
                <span
                    class="border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-500 focus:text-gray-700 focus:border-gray-500 hover:bg-gray-200 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-300 dark:hover:bg-gray-900 -mt-px border-t-2 pt-6 pb-6 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition"
                >
                    ...
                </span>
            </div>
            <div class="w-0 flex-1 flex justify-end">
                <button type="button" v-if="currentYear < yearRange[yearRange.length - 1]" aria-label="Next"
                        @click="++currentYear"
                        class="-mt-px border-t-2 pr-6 border-transparent pt-6 pb-6 pl-1 inline-flex items-center text-sm leading-5 font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-400 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-300 transition"
                >
                    {{ $t('pagination.next') }}
                    <icon name="arrow-right" class-icon="ml-3 h-5 w-5" fill="currentColor"/>
                </button>
            </div>
        </div>
    </app-layout>
</template>

<script>
import Store from '@/Models/Store';

export default {
    props: {
        user: Object,
        store: Store,
    },

    data: function() {
        return {
            currentYear: (new Date()).getFullYear(),
            monthRange: _.range(1, 13),
            isLoading: true,
            years: {},
        };
    },

    metaInfo() {
        return {
            title: this.$t('general.profit_and_loss'),
        };
    },

    async created() {
        this.isLoading = true;
        await this.fetchYear(this.currentYear);
        this.isLoading = false;
    },

    computed: {
        yearRange() {
            return _.range(this.currentYear - 3, this.currentYear + 4);
        },
    },

    methods: {
        hasBudget(year, month) {
            let date = moment.utc(month + '-' + year, 'M-Y').startOf('month');
            let budget = this.years[year].budgets.find(budget => budget.year === year);

            if (!budget) {
                return false;
            }

            return !!budget.entries.find(entry => entry.isFilled && moment.utc(entry.date).isSame(date));
        },

        hasProjection(year, month) {
            let date = moment.utc(month + '-' + year, 'M-Y').startOf('month');

            return !!this.years[year].projections.find(
                projection => projection.isFilled && moment.utc(projection.date).isSame(date));
        },

        getExtrapolationCount(year, month) {
            let startOfMonth = moment.utc(month + '-' + year, 'M-Y').startOf('month');
            let endOfMonth = moment.utc(month + '-' + year, 'M-Y').endOf('month');

            return this.years[year].extrapolations.filter(extrapolation => {
                return extrapolation.isFilled && moment.utc(extrapolation.date).isBetween(startOfMonth, endOfMonth);
            }).length;
        },

        hasProfitAndLoss(year, month) {
            let date = moment.utc(month + '-' + year, 'M-Y').startOf('month');

            return !!this.years[year].profit_and_losses.find(
                profitAndLoss => profitAndLoss.isFilled && moment.utc(profitAndLoss.date).isSame(date));
        },

        hasActual(year, month) {
            let date = moment.utc(month + '-' + year, 'M-Y').startOf('month');

            return !!this.years[year].actuals.find(actual => actual.isFilled && moment.utc(actual.date).isSame(date));
        },

        fetchYear(year) {
            return axios.get(route('api.stores.profit-and-loss.show', {store: this.store.id, year: year}))
                .then(response => {
                    this.years[year] = response.data;
                });
        },
    },

    watch: {
        async currentYear(year) {
            this.isLoading = true;

            if (!this.years.hasOwnProperty(year)) {
                await this.fetchYear(year);
            }

            this.isLoading = false;
        },
    },
};
</script>
